import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Wrappers para as Cloud Functions de execução do módulo SI (Categoria 1 — funções
 * novas, exclusivas do SI). Mesmo padrão de chamada já usado em MigrationTools.
 */

export async function concluirSolicitacao(solicitacaoId: string): Promise<void> {
  const functions = getFunctions();
  const fn = httpsCallable(functions, 'concluirSolicitacao');
  await fn({ solicitacaoId });
}

export interface AtivoGlpiEncontrado {
  glpiItemType: 'Computer' | 'Monitor' | 'Phone';
  glpiItemId: string;
  patrimonio: string | null;
  nSerie: string | null;
  modelo: string | null;
  fabricante: string | null;
  /** Nome de login do usuário GLPI associado ao ativo (campo "Usuário" do GLPI). */
  usuario: string | null;
  /** Nome completo cadastrado no GLPI (não é necessariamente um colaborador do Digital). */
  usuarioNomeCompleto: string | null;
  /** Status do ativo no GLPI (hierárquico, ex. "SWAP > Em Uso"). */
  status: string | null;
  /** Etiqueta (plugin_tag) — nem sempre preenchida, mais comum em Computer. */
  etiqueta: string | null;
  /** Só preenchidos para Computer — Monitor/Phone não têm esses componentes no GLPI. */
  processador: string | null;
  memoria: string | null;
  tamanhoDisco: string | null;
  /** Sistema operacional — só existe para Computer. */
  sistemaOperacional: string | null;
}

export async function consultarAtivoGlpi(params: {
  patrimonio?: string;
  nSerie?: string;
}): Promise<AtivoGlpiEncontrado | null> {
  const functions = getFunctions();
  const fn = httpsCallable<typeof params, { encontrado: boolean; ativo: AtivoGlpiEncontrado | null }>(
    functions,
    'consultarAtivoGlpi'
  );
  const result = await fn(params);
  return result.data.encontrado ? result.data.ativo : null;
}

export interface EquipamentoResumo {
  id: string;
  tipo: string | null;
  patrimonio: string | null;
  modelo: string | null;
  nSerie: string | null;
  /** Opcionais: a function antiga não mandava (ver descreverEquipamentoResumo). */
  fabricante?: string | null;
  linha?: string | null;
  operadora?: string | null;
  /** Texto do cadastro (ex. "Windows 11 Pro") — sugere o SO na Troca (sugestaoSoParaTroca). */
  sistemaOperacional?: string | null;
}

/** Usado ao abrir Mudança de Projeto/Troca de Equipamento — sugere qual(is) equipamento(s)
 * devolver, hoje vinculados ao colaborador-alvo (ver onMovimentacaoWriteAplicarEquipamentoSugerido). */
export async function buscarEquipamentosDoColaborador(colaboradorId: string): Promise<EquipamentoResumo[]> {
  const functions = getFunctions();
  const fn = httpsCallable<{ colaboradorId: string }, { equipamentos: EquipamentoResumo[] }>(
    functions,
    'buscarEquipamentosDoColaborador'
  );
  const result = await fn({ colaboradorId });
  return result.data.equipamentos;
}

export interface PossivelMovimentacaoDoEquipamento {
  solicitacaoId: string;
  movimentacaoId: string;
  tipoFluxo: string | null;
  colaboradorNome: string | null;
  /** "patrimônio" ou "nº de série" — por que pode ser deste equipamento. */
  casamento: string;
  /** millis */
  data: number | null;
}

/** "Buscar histórico nas Solicitações" — ver functions/src/si/recuperarHistoricoEquipamento.ts.
 * `confirmarMovimentacaoIds`: possíveis que o SI confirmou serem deste equipamento. */
export async function recuperarHistoricoEquipamento(params: {
  equipamentoId: string;
  confirmarMovimentacaoIds?: string[];
}): Promise<{ recuperados: number; possiveis: PossivelMovimentacaoDoEquipamento[] }> {
  const fn = httpsCallable<typeof params, { recuperados: number; possiveis: PossivelMovimentacaoDoEquipamento[] }>(
    getFunctions(),
    'recuperarHistoricoEquipamento'
  );
  return (await fn(params)).data;
}

/** 'excluido' = casou com um ativo excluído no Digital — não é recriado (restaure, se foi engano). */
export type AcaoImportacaoGlpi = 'novo' | 'completar' | 'sem_alteracao' | 'conflito' | 'excluido';

/** Linha da prévia da importação do GLPI (ver functions/src/si/importarInventarioGlpi.ts). */
export interface ItemPreviaImportacaoGlpi {
  chave: string;
  usuarioLogin: string | null;
  itemtype: string;
  tipo: string | null;
  patrimonio: string | null;
  nSerie: string | null;
  modelo: string | null;
  fabricante: string | null;
  statusGlpi: string | null;
  statusCustodia: string | null;
  situacaoDetalhe: string | null;
  usuarioGlpi: string | null;
  colaboradorAtualId: string | null;
  projetoClienteId: string | null;
  propriedade: 'raro' | 'cliente' | null;
  clienteProprietarioId: string | null;
  acao: AcaoImportacaoGlpi;
  /** Cadastro do Digital com que casou (quando não é novo). */
  existente: ResumoCadastroDigital | null;
  camposACompletar: string[];
  /** Valor diferente no Digital e no GLPI — fica o do Digital, a não ser que o SI escolha o do GLPI. */
  divergencias: Array<{ campo: string; digital: string; glpi: string }>;
  motivoConflito: string | null;
  /** Conflito entre dois cadastros do Digital: os candidatos pra escolher. */
  candidatos: ResumoCadastroDigital[];
  /** "Em uso" no GLPI por alguém que não existe no Digital → veio como Inativo · Não
   * localizado. Dizendo quem é, volta pra Em uso. */
  naoLocalizadoSemColaborador: boolean;
  /** `campo` = o ajuste que resolve o aviso. */
  avisos: Array<{ campo: string; mensagem: string }>;
}

export interface ResumoCadastroDigital {
  id: string;
  patrimonio: string | null;
  nSerie: string | null;
}

/** O que o SI decidiu na revisão (ver planejarGravacao no servidor). */
export interface DecisaoImportacaoGlpi {
  chave: string;
  ajustes?: Record<string, string | null>;
  usarGlpi?: string[];
  /** Conflito: 'manter_digital' | 'usar_glpi' | 'ligar:<id>'. */
  resolucao?: string | null;
}

export interface ResultadoImportacaoGlpi {
  criados: number;
  completados: number;
  ignorados: Array<{ chave: string; motivo: string }>;
}

/** Ler o GLPI inteiro leva mais de 1 min (576+ computadores) — o padrão do SDK é 70 s. */
const TIMEOUT_IMPORTACAO_MS = 540_000;

/** Significado de um usuário do GLPI que não é pessoa (ex.: DEVOLVIDO PINE → Devolvido ao Cliente · Pine). */
export interface SignificadoUsuarioGlpi {
  statusCustodia?: string | null;
  situacaoDetalhe?: string | null;
  clienteId?: string | null;
  /** Com "Em manutenção": onde (ex.: "Oficina do SMART"). */
  fornecedorManutencao?: string | null;
}

/** Marcação lembrada de um login do GLPI: "não é uma pessoa" (+ significado) ou "é tal colaborador". */
export interface ConfigUsuarioGlpi extends SignificadoUsuarioGlpi {
  naoEPessoa: boolean;
  colaboradorId?: string | null;
  colaboradorNome?: string | null;
}

export interface UsuarioGlpiNaoEncontrado {
  login: string;
  nome: string;
  quantidade: number;
  /** Já marcado antes como "não é uma pessoa" (lembrado em si_config_usuarios_glpi). */
  config: ConfigUsuarioGlpi | null;
  /** Sugestão pelo nome — o SI confirma ou corrige. */
  sugestao: SignificadoUsuarioGlpi | null;
}

export interface PreviaImportacaoGlpi {
  itens: ItemPreviaImportacaoGlpi[];
  usuariosNaoEncontrados: UsuarioGlpiNaoEncontrado[];
}

export async function previaImportacaoGlpi(): Promise<PreviaImportacaoGlpi> {
  const fn = httpsCallable<void, PreviaImportacaoGlpi>(getFunctions(), 'previaImportacaoGlpi', {
    timeout: TIMEOUT_IMPORTACAO_MS,
  });
  return (await fn()).data;
}

export async function aplicarImportacaoGlpi(decisoes: DecisaoImportacaoGlpi[]): Promise<ResultadoImportacaoGlpi> {
  const fn = httpsCallable<{ decisoes: DecisaoImportacaoGlpi[] }, ResultadoImportacaoGlpi>(
    getFunctions(),
    'aplicarImportacaoGlpi',
    { timeout: TIMEOUT_IMPORTACAO_MS }
  );
  return (await fn({ decisoes })).data;
}

/** Item da aba "Meus equipamentos" (Portal do SI) — ver functions/src/si/meusEquipamentos.ts. */
export interface MeuEquipamento {
  id: string;
  tipo: string | null;
  patrimonio: string | null;
  modelo: string | null;
  nSerie: string | null;
  fabricante: string | null;
  sistemaOperacional: string | null;
  linha: string | null;
  operadora: string | null;
  numeroCartao: string | null;
  statusCustodia: string | null;
  transitoSentido: 'envio' | 'devolucao' | null;
  transitoSolicitacaoId: string | null;
  propriedade: string | null;
  clienteProprietarioNome: string | null;
  projetoNome: string | null;
  /** millis */
  desde: number | null;
  termoUrl: string | null;
}

export async function listarMeusEquipamentos(): Promise<MeuEquipamento[]> {
  const fn = httpsCallable<void, { equipamentos: MeuEquipamento[] }>(getFunctions(), 'listarMeusEquipamentos');
  const result = await fn();
  return result.data.equipamentos;
}

/** E-mails de quem tem si_rh — só quem tem si_rh pode chamar. */
export async function listarTimeRh(): Promise<string[]> {
  const fn = httpsCallable<void, { emails: string[] }>(getFunctions(), 'listarTimeRh');
  const result = await fn();
  return result.data.emails;
}

/** Abertura com e-mail para o Cliente: o Digital já tem o RG da pessoa? Pelo perfil
 * (colaboradorId) ou, na Admissão, pelo pré-cadastro (emailPessoal). Só sim/não — quem abre
 * não lê esses dados (ver DadosClienteAbertura). */
export async function verificarDadosEmailCliente(
  pessoa: { colaboradorId: string } | { emailPessoal: string }
): Promise<{ rgCadastrado: boolean }> {
  const fn = httpsCallable<typeof pessoa, { rgCadastrado: boolean }>(getFunctions(), 'verificarDadosEmailCliente');
  return (await fn(pessoa)).data;
}
